import alunoService from '@/services/alunoService';
import coordenadorService from '@/services/coordenadorService';
import professorService from '@/services/professorService';
import { defineStore } from 'pinia';
import { useAuthStore } from './AuthStore';

export interface Curso {
    id: number;
    nome: string;
    codigo_sigaa: string;
    nivel?: string;
    coordenador_id?: number;
    ativo?: boolean;
}

interface CursoState {
    cursoAtual: Curso | null;
    cursosDisponiveis: Curso[];
    loading: boolean;
    initialized: boolean;
    error: string | null;
}

export const useCursoStore = defineStore('curso', {
    state: (): CursoState => ({
        cursoAtual: null,
        cursosDisponiveis: [],
        loading: false,
        initialized: false,
        error: null,
    }),

    getters: {
        getCursoAtual: (state): Curso | null => state.cursoAtual,

        hasCursos: (state): boolean => state.cursosDisponiveis.length > 0,

        hasMultiplosCursos: (state): boolean => state.cursosDisponiveis.length > 1,

        hasError: (state): boolean => !!state.error,

        isReady: (state): boolean => state.initialized && !state.loading,
    },

    actions: {
        async loadCursosUsuario() {
            const authStore = useAuthStore();

            if (!authStore.isAuthenticated) {
                console.warn('Usuário não autenticado. Não é possível carregar cursos.');
                this.error = 'Usuário não autenticado';
                return;
            }

            try {
                this.loading = true;
                this.error = null;
                let cursos: any = [];

                const currentRole = authStore.activeRole || authStore.getDefaultRole();

                if (currentRole === 'coordenador' || currentRole === 'secretaria' || currentRole === 'coordenador_tcc') {
                    const response = await coordenadorService.getMeuCurso();
                    cursos = response.data;
                } else if (currentRole === 'orientador' || currentRole === 'professor') {
                    const response = await professorService.getMeusCursos();
                    cursos = response.data;
                } else if (currentRole === 'aluno') {
                    const response = await alunoService.getMeusCursos();
                    cursos = response.data;
                } else if (currentRole === 'admin') {
                    this.error = null;
                    this.cursosDisponiveis = [];
                    this.initialized = true;
                    return;
                } else {
                    this.error = 'Perfil sem cursos disponíveis';
                    this.cursosDisponiveis = [];
                    this.initialized = true;
                    return;
                }

                this.cursosDisponiveis = Array.isArray(cursos) ? cursos : (cursos ? [cursos] : []);

                if (this.cursosDisponiveis.length === 0) {
                    this.error = 'Nenhum curso vinculado ao seu perfil';
                }

                if (!this.cursoAtual && this.cursosDisponiveis.length > 0) {
                    await this.initializeCurso();
                }

                this.initialized = true;
            } catch (error: any) {
                console.error('Erro ao carregar cursos do usuário:', error);
                this.error = error.response?.data?.message || 'Erro ao carregar cursos';
                this.cursosDisponiveis = [];
            } finally {
                this.loading = false;
            }
        },

        setCursoAtual(curso: Curso | null) {
            if (curso && !this.cursosDisponiveis.some(c => c.id === curso.id)) {
                console.error('Tentativa de selecionar curso não disponível:', curso);
                this.error = 'Curso selecionado não está disponível';
                return;
            }

            this.cursoAtual = curso;
            this.error = null;
        },

        async initializeCurso() {
            const authStore = useAuthStore();

            if (!authStore.user?.id) {
                console.warn('Usuário não identificado. Não é possível inicializar curso.');
                return;
            }

            if (this.cursosDisponiveis.length === 0 && !this.initialized) {
                await this.loadCursosUsuario();
            }

            if (this.cursosDisponiveis.length > 0) {
                this.setCursoAtual(this.cursosDisponiveis[0]);
            } else {
                console.warn('Nenhum curso disponível para inicializar');
            }
            this.initialized = true;
        },

        clearCurso() {
            this.cursoAtual = null;
            this.cursosDisponiveis = [];
            this.initialized = false;
            this.error = null;
        },

        clearError() {
            this.error = null;
        },

        async reloadCursos() {
            this.cursoAtual = null;
            this.cursosDisponiveis = [];
            this.initialized = false;
            await this.loadCursosUsuario();
        },
    },
});
